import { Langfuse } from 'langfuse';

// Initialize Langfuse client
const langfuse = new Langfuse({
  publicKey: process.env.REACT_APP_LANGFUSE_PUBLIC_KEY || '',
  secretKey: process.env.REACT_APP_LANGFUSE_SECRET_KEY || '',
  baseUrl: process.env.REACT_APP_LANGFUSE_BASE_URL || 'https://cloud.langfuse.com',
});

// Session trace reference
let currentTrace: ReturnType<typeof langfuse.trace> | null = null;
let sessionStartTime: Date | null = null;

// Track pending user messages to measure response latency
let pendingUserMessage: {
  content: string;
  startTime: Date;
  messageId: string;
} | null = null;

interface SessionMetadata {
  sessionId: number;
  patientId: number;
  patientName: string;
  patientAge: number;
  patientGender: string;
  communicationMode: 'audio' | 'text';
  practitionerType?: string;
}

interface MessageData {
  role: 'user' | 'assistant';
  content: string;
  messageId?: string;
}

/**
 * Start a new Langfuse trace for a chat session
 */
export const startSessionTrace = (metadata: SessionMetadata): string => {
  sessionStartTime = new Date();

  currentTrace = langfuse.trace({
    name: 'anzu-realtime-conversation',
    userId: metadata.patientId.toString(),
    sessionId: metadata.sessionId.toString(),
    metadata: {
      patientName: metadata.patientName,
      patientAge: metadata.patientAge,
      patientGender: metadata.patientGender,
      communicationMode: metadata.communicationMode,
      practitionerType: metadata.practitionerType,
      startTime: sessionStartTime.toISOString(),
    },
    tags: [metadata.communicationMode, 'patient-chat'],
  });

  console.log('[Langfuse] Session trace started:', currentTrace.traceId);
  return currentTrace.traceId;
};

/**
 * Log a message (user or assistant) to the current trace
 * For user messages: starts timing for response latency
 * For assistant messages: completes the generation with latency data
 */
export const logMessage = (message: MessageData): void => {
  if (!currentTrace) {
    console.warn('[Langfuse] No active trace. Call startSessionTrace first.');
    return;
  }

  const now = new Date();

  if (message.role === 'user') {
    // Store user message to track response latency
    pendingUserMessage = {
      content: message.content,
      startTime: now,
      messageId: message.messageId || now.toISOString(),
    };

    // Log user message as a span (for visibility in timeline)
    currentTrace.span({
      name: 'user-message',
      startTime: now,
      endTime: now,
      input: message.content,
      metadata: {
        role: 'user',
        messageId: pendingUserMessage.messageId,
      },
    });

    console.log('[Langfuse] Logged user message, waiting for response...');
  } else {
    // Assistant response - create generation with proper latency calculation
    const userMessageTime = pendingUserMessage?.startTime;
    const latencyMs = userMessageTime ? now.getTime() - userMessageTime.getTime() : 0;

    // Use user message time + 1ms offset as startTime
    // The +1ms ensures assistant-response appears AFTER user-message in timeline
    // while keeping latency calculation essentially accurate
    const generationStartTime = userMessageTime
      ? new Date(userMessageTime.getTime() + 1)
      : now;

    currentTrace.generation({
      name: 'assistant-response',
      startTime: generationStartTime,
      endTime: now,
      input: pendingUserMessage?.content || '',
      output: message.content,
      model: 'gpt-realtime',
      metadata: {
        role: 'assistant',
        messageId: message.messageId || now.toISOString(),
        userMessageId: pendingUserMessage?.messageId,
        latencyMs,
        latencySeconds: Math.round(latencyMs / 1000 * 100) / 100,
      },
    });

    console.log(`[Langfuse] Logged assistant response. Latency: ${latencyMs}ms`);

    // Clear pending message
    pendingUserMessage = null;
  }
};

/**
 * End the current session trace
 */
export const endSessionTrace = (messageCount: number, saveStatus: 'success' | 'failed'): void => {
  if (!currentTrace) {
    console.warn('[Langfuse] No active trace to end.');
    return;
  }

  const endTime = new Date();
  const durationMs = sessionStartTime ? endTime.getTime() - sessionStartTime.getTime() : 0;

  currentTrace.update({
    metadata: {
      endTime: endTime.toISOString(),
      durationMs,
      durationSeconds: Math.round(durationMs / 1000),
      messageCount,
      saveStatus,
    },
  });

  // Flush to ensure data is sent
  langfuse.flush();

  console.log('[Langfuse] Session trace ended. Duration:', Math.round(durationMs / 1000), 'seconds');

  // Reset state
  currentTrace = null;
  sessionStartTime = null;
  pendingUserMessage = null;
};

/**
 * Log an error event
 */
export const logError = (errorType: string, errorMessage: string, metadata?: Record<string, unknown>): void => {
  if (!currentTrace) {
    console.warn('[Langfuse] No active trace for error logging.');
    return;
  }

  currentTrace.event({
    name: 'error',
    metadata: {
      errorType,
      errorMessage,
      timestamp: new Date().toISOString(),
      ...metadata,
    },
  });

  console.log('[Langfuse] Error logged:', errorType);
};

/**
 * Flush pending events (call before page unload)
 */
export const flushLangfuse = (): void => {
  langfuse.flush();
};

export { langfuse };
